/**
 * Живе оновлення «Сьогодні» (tasks-today.md, розд. 6) — один раз на оболонку, а не в панелі:
 * лічильник «Задачі N» має бути свіжим і тоді, коли панель згорнуто.
 * - Події SSE `record.*` задач і `sla.*` будь-якого запису (лише id, SEC-043) — перечитати з
 *   дебаунсом 300 мс. Відлуння власної зміни — ні: після неї панель перечитується сама.
 * - Північ (`dayEnd`): перечитати — межі дня нові, невиконане сьогоднішнє стає простроченим.
 * - `visibilitychange`: прихована вкладка сповільнює таймери, тож на поверненні — «зараз» і
 *   перечитування.
 * - Перехід рядка в «Прострочено» о `overdueAt` і зсув лінії «зараз» — без запиту: таймер на
 *   найближчий такий момент оновлює «зараз» (`refreshNow`), секції перебудовує `arrange`. Межу
 *   рахуємо від «зараз», яке бачить інтерфейс (`currentNow`), а не від настінного часу: дані, що
 *   прийшли вже після межі (повільне завантаження між щохвилинними тіками), оновлюють «зараз»
 *   одразу, а не за хвилину. Кожна зміна «зараз» (межа, тік, повернення на вкладку) ставить таймер
 *   на наступну межу: інакше друга задача чекала б тіку.
 */
import { skipToken, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { currentNow, onNowChange, refreshNow } from '../../app/now.ts';
import { onRecordEvent } from '../../platform/events.ts';
import { nextBoundary, type TodayData } from './model.ts';
import { TASK_ENTITY, type TodaySource } from './source.ts';

export const LIVE_DEBOUNCE_MS = 300;
/** Межа `setTimeout` (2^31 − 1 мс): далі таймер спрацює одразу. */
const MAX_TIMER_MS = 2_147_483_647;
const NO_TODAY = ['today', 'none'] as const;

/** Чи стосується подія «Сьогодні»: задача або таймер першої реакції запису. */
export const touchesToday = (event: { type: string; entity: string | null }) =>
  event.entity === TASK_ENTITY || event.type.startsWith('sla.');

export function useTodayLive(source: TodaySource | null) {
  const client = useQueryClient();
  // Лише кеш: запит роблять панель, перемикач і навігація, коли їм потрібні дані. Той самий
  // `queryFn`, що в них: спостерігач без нього міг би стати джерелом опцій для перечитування.
  const { data } = useQuery<TodayData>({
    queryKey: source?.key ?? NO_TODAY,
    queryFn: source ? ({ signal }) => source.fetch(signal) : skipToken,
    enabled: false,
  });
  const key = source?.key;

  useEffect(() => {
    if (!source) return;
    const reload = () => void client.invalidateQueries({ queryKey: source.key, exact: true });
    let timer: ReturnType<typeof setTimeout> | undefined;
    const stopEvents = source.live
      ? onRecordEvent((event, own) => {
          if (own || !touchesToday(event)) return;
          clearTimeout(timer);
          timer = setTimeout(reload, LIVE_DEBOUNCE_MS);
        })
      : () => {};
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      refreshNow();
      reload();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearTimeout(timer);
      stopEvents();
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [client, source]);

  useEffect(() => {
    if (!data || !key) return;
    const end = Date.parse(data.dayEnd);
    let timer: ReturnType<typeof setTimeout> | undefined;
    const schedule = () => {
      clearTimeout(timer);
      const shown = currentNow();
      // Північ уже показано і перечитування запитано: далі — нові дані з новою `dayEnd`.
      if (shown >= end) return;
      // Межа, що вже минула (дані прийшли пізніше, таймер спрацював на мить раніше), — одразу.
      const at = nextBoundary(data, shown);
      timer = setTimeout(
        () => {
          refreshNow();
          if (Date.now() >= end) void client.invalidateQueries({ queryKey: key, exact: true });
        },
        Math.min(Math.max(0, at - Date.now()), MAX_TIMER_MS),
      );
    };
    schedule();
    const stop = onNowChange(schedule);
    return () => {
      clearTimeout(timer);
      stop();
    };
  }, [client, data, key]);
}
